class KabluchkaCollectionToolbar extends HTMLElement {
  constructor() {
    super();

    this.disclosures = [];
    this.disclosureAnimations = new WeakMap();
    this.disclosureOperations = new WeakMap();
    this.transitionSequence = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.filterForm = this.querySelector('[data-native-filter-form]');

    if (this.applyDefaultAvailability()) return;

    this.querySelectorAll('[data-collection-navigation]').forEach((select) => this.enhanceNavigationSelect(select));
    this.querySelectorAll('.ke-collection-toolbar__disclosure').forEach((disclosure) => this.bindDisclosure(disclosure));

    this.handleDocumentClick = (event) => {
      if (!this.contains(event.target)) this.closeDisclosures();
    };
    document.addEventListener('click', this.handleDocumentClick);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const openDisclosure = this.disclosures.find((disclosure) => disclosure.open);
      if (!openDisclosure) return;

      event.preventDefault();
      this.closeDisclosure(openDisclosure, { restoreFocus: true });
    });

    if (this.filterForm) {
      this.filterForm.addEventListener('submit', (event) => this.submitFilterForm(event));
      this.filterForm.addEventListener('change', () => this.filterForm.requestSubmit());
    }

    this.priceRange = this.querySelector('[data-price-range]');
    if (this.priceRange) this.bindPriceRange();
  }

  disconnectedCallback() {
    if (this.handleDocumentClick) document.removeEventListener('click', this.handleDocumentClick);
  }

  enhanceNavigationSelect(select) {
    const wrapper = select.closest('.ke-collection-toolbar__select-wrap');
    if (!wrapper) return;

    const options = Array.from(select.options);
    const selectedOption = options.find((option) => option.selected) || options[0];
    const selectedIndex = Math.max(options.indexOf(selectedOption), 0);
    const disclosure = document.createElement('details');
    disclosure.className = 'ke-collection-toolbar__disclosure ke-collection-toolbar__disclosure--navigation';
    disclosure.dataset.navigationDisclosure = '';

    const summary = document.createElement('summary');
    summary.className = 'ke-collection-toolbar__control';
    if (selectedIndex > 0) summary.classList.add('is-selected');

    const label = document.createElement('span');
    label.textContent = selectedOption?.textContent.trim() || '';
    summary.append(label, this.createCaret());

    const popover = document.createElement('div');
    popover.className = 'ke-collection-toolbar__popover ke-collection-toolbar__popover--navigation';
    popover.setAttribute('role', 'list');
    popover.setAttribute('aria-label', select.getAttribute('aria-label') || label.textContent);

    options.forEach((option, index) => {
      const item = option.disabled || !option.value ? document.createElement('span') : document.createElement('a');
      item.className = 'ke-collection-toolbar__navigation-option';
      item.textContent = option.textContent.trim();

      if (option.disabled || !option.value) {
        item.classList.add('is-disabled');
        item.setAttribute('aria-disabled', 'true');
      } else {
        item.href = this.withAvailabilitySelection(option.value);
      }

      if (index === selectedIndex) {
        item.classList.add('is-selected');
        if (item.tagName === 'A') item.setAttribute('aria-current', 'page');
      }

      popover.append(item);
    });

    disclosure.append(summary, popover);
    wrapper.hidden = true;
    wrapper.style.display = 'none';
    wrapper.insertAdjacentElement('afterend', disclosure);
  }

  applyDefaultAvailability() {
    const parameter = this.dataset.defaultAvailabilityParam;
    const defaultValue = this.dataset.defaultAvailabilityValue;
    if (!parameter || !defaultValue) return false;

    const url = new URL(window.location.href);
    if (url.searchParams.has(parameter)) return false;

    url.searchParams.append(parameter, defaultValue);
    url.searchParams.delete('page');
    window.location.replace(url.toString());
    return true;
  }

  withAvailabilitySelection(destination) {
    const parameter = this.dataset.defaultAvailabilityParam;
    const defaultValue = this.dataset.defaultAvailabilityValue;
    if (!parameter || !defaultValue) return destination;

    const currentUrl = new URL(window.location.href);
    const destinationUrl = new URL(destination, window.location.origin);
    const selectedValues = currentUrl.searchParams.getAll(parameter);

    destinationUrl.searchParams.delete(parameter);
    (selectedValues.length > 0 ? selectedValues : [defaultValue]).forEach((value) => {
      destinationUrl.searchParams.append(parameter, value);
    });
    destinationUrl.searchParams.delete('page');

    return destinationUrl.toString();
  }

  bindDisclosure(disclosure) {
    const summary = disclosure.querySelector(':scope > summary');
    const popover = disclosure.querySelector(':scope > .ke-collection-toolbar__popover');
    if (!summary || !popover) return;

    summary.setAttribute('aria-expanded', String(disclosure.open));
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleDisclosure(disclosure);
    });

    this.disclosures.push(disclosure);
  }

  createCaret() {
    const caret = document.createElement('span');
    caret.className = 'ke-collection-toolbar__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<svg viewBox="0 0 10 6" focusable="false"><path d="m1 1 4 4 4-4" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return caret;
  }

  async toggleDisclosure(disclosure) {
    const sequence = ++this.transitionSequence;

    if (disclosure.open) {
      await this.closeDisclosure(disclosure);
      return;
    }

    const previouslyOpen = this.disclosures.find((item) => item.open && item !== disclosure);
    if (previouslyOpen) await this.closeDisclosure(previouslyOpen);
    if (sequence !== this.transitionSequence) return;

    await this.openDisclosure(disclosure);
  }

  async openDisclosure(disclosure) {
    if (disclosure.open) return;

    const summary = disclosure.querySelector(':scope > summary');
    const popover = disclosure.querySelector(':scope > .ke-collection-toolbar__popover');
    const operation = Symbol('open');
    this.disclosureOperations.set(disclosure, operation);

    disclosure.open = true;
    disclosure.dataset.disclosureState = 'opening';
    summary?.setAttribute('aria-expanded', 'true');

    await this.animatePopover(
      disclosure,
      popover,
      [
        { opacity: 0, transform: 'translateY(-0.8rem) scale(0.985)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 190, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );

    if (this.disclosureOperations.get(disclosure) === operation) {
      disclosure.dataset.disclosureState = 'open';
    }
  }

  async closeDisclosure(disclosure, { restoreFocus = false } = {}) {
    if (!disclosure.open) return;

    const summary = disclosure.querySelector(':scope > summary');
    const popover = disclosure.querySelector(':scope > .ke-collection-toolbar__popover');
    const operation = Symbol('close');
    this.disclosureOperations.set(disclosure, operation);
    disclosure.dataset.disclosureState = 'closing';
    summary?.setAttribute('aria-expanded', 'false');

    await this.animatePopover(
      disclosure,
      popover,
      [
        { opacity: 1, transform: 'translateY(0) scale(1)' },
        { opacity: 0, transform: 'translateY(-0.6rem) scale(0.985)' },
      ],
      { duration: 130, easing: 'cubic-bezier(0.4, 0, 1, 1)' }
    );

    if (this.disclosureOperations.get(disclosure) !== operation) return;

    disclosure.open = false;
    delete disclosure.dataset.disclosureState;
    if (restoreFocus) summary?.focus();
  }

  animatePopover(disclosure, popover, options, timing) {
    const activeAnimation = this.disclosureAnimations.get(disclosure);
    activeAnimation?.cancel();

    if (!popover || this.reducedMotion.matches || typeof popover.animate !== 'function') {
      return Promise.resolve();
    }

    const animation = popover.animate(options, { ...timing, fill: 'both' });
    this.disclosureAnimations.set(disclosure, animation);

    return animation.finished
      .catch(() => undefined)
      .finally(() => {
        if (this.disclosureAnimations.get(disclosure) === animation) {
          animation.cancel();
          this.disclosureAnimations.delete(disclosure);
        }
      });
  }

  closeDisclosures() {
    this.transitionSequence += 1;
    this.disclosures.forEach((disclosure) => {
      if (disclosure.open) this.closeDisclosure(disclosure);
    });
  }

  submitFilterForm(event) {
    event.preventDefault();

    const formData = new FormData(this.filterForm);
    const minimum = this.filterForm.querySelector('[data-price-min]');
    const maximum = this.filterForm.querySelector('[data-price-max]');

    if (
      minimum &&
      maximum &&
      Number(minimum.value) <= Number(minimum.min) &&
      Number(maximum.value) >= Number(maximum.max)
    ) {
      formData.delete(minimum.name);
      formData.delete(maximum.name);
    }

    formData.delete('page');

    const destination = new URL(this.filterForm.action, window.location.origin);
    const parameters = new URLSearchParams();
    formData.forEach((value, key) => parameters.append(key, value));
    destination.search = parameters.toString();
    window.location.assign(destination.toString());
  }

  bindPriceRange() {
    const minimum = this.priceRange.querySelector('[data-price-min]');
    const maximum = this.priceRange.querySelector('[data-price-max]');
    const minimumOutput = this.querySelector('[data-price-min-output]');
    const maximumOutput = this.querySelector('[data-price-max-output]');
    const currency = minimumOutput.textContent.trim().split(/\s+/).at(-1);
    const locale = document.documentElement.lang || 'uk';

    const format = (value) => `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)} ${currency}`;
    const update = (changedInput) => {
      const min = Number(minimum.value);
      const max = Number(maximum.value);

      if (min > max) {
        if (changedInput === minimum) maximum.value = minimum.value;
        else minimum.value = maximum.value;
      }

      const limit = Math.max(Number(maximum.max), 1);
      this.priceRange.style.setProperty('--ke-price-min', `${(Number(minimum.value) / limit) * 100}%`);
      this.priceRange.style.setProperty('--ke-price-max', `${(Number(maximum.value) / limit) * 100}%`);
      minimumOutput.textContent = format(Number(minimum.value));
      maximumOutput.textContent = format(Number(maximum.value));
    };

    minimum.addEventListener('input', () => update(minimum));
    maximum.addEventListener('input', () => update(maximum));
    update();
  }
}

if (!customElements.get('kabluchka-collection-toolbar')) {
  customElements.define('kabluchka-collection-toolbar', KabluchkaCollectionToolbar);
}
